import { useContext, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Col, Row, Typography } from '@arkestro/arkestro-design-system';

import { getCompanyVendorOrganizationProfile , updateVendorOrganization } from '@api/VendorDBApi';
import { updateVendorProfile } from '@api/VendorProfileDbApi';
import { alertMessage } from '@utils/common';
import Loading from '@shared/Loading/Loading';
import { UserContext } from '@providers/UserProvider/UserProvider';
import FloatingPanel from '@components/on_board/FloatingPanel/FloatingPanel';
import Footer from '@components/on_board/Footer/Footer';

import style from './VendorOnboardReview.module.scss';

interface NewVendorOrgProps {
  description: string;
  name: string;
  publicListed: boolean;
  website: string;
}

interface UserParams {
  firstName: string;
  isFreeEmail: boolean;
  jobTitle: string;
  lastName: string;
}

interface Props {
  currentOrgStep: string;
  currentStepName: string;
  newOrgCreated: boolean;
  newVendorOrgParams: NewVendorOrgProps | undefined
  progress: boolean;
  setProperty: (params: any) => void;
  updateOnboardStep: (param: string) => void;
  updateOrgId: number | undefined;
  userParams: UserParams;
}

export const VendorOnboardReview = ({
  currentOrgStep,
  currentStepName,
  updateOnboardStep,
  progress,
  setProperty,
  newVendorOrgParams,
  newOrgCreated,
  updateOrgId,
  userParams
}: Props) => {
  const { t } = useTranslation('common');
  const currentUser = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorOrg, setVendorOrg] = useState({ description: '', name: '', website: '' });

  useEffect(() => {
    if (newVendorOrgParams) {
      setVendorOrg(newVendorOrgParams);
    } else {
      setLoading(true);
      getCompanyVendorOrganizationProfile(updateOrgId, '')
        .then(response => {
          if (!response.success) return;

          setVendorOrg(response.vendor_organization);
        })
        .finally(() => setLoading(false));
    }
  }, [currentUser.organization_id]);

  const submitVendorProfile = async (vendorOrgId: number) => {
    const profileResponse = await updateVendorProfile({ email: currentUser.email, vendorOrgId, ...userParams }, userParams.isFreeEmail);

    if (!profileResponse.success) {
      alertMessage(profileResponse.errors[0], 'error', 10);
      setIsSubmitting(false);
      return;
    }
    setProperty({ currentOrgStep: 'vendor_welcome' });
    updateOnboardStep(currentStepName);
  };

  const submitVendorOrg = async () => {
    const response = await updateVendorOrganization(newVendorOrgParams);

    if (!response.success) {
      alertMessage(response.errors[0], 'error', 10);
      setIsSubmitting(false);
      return;
    }

    const vendorOrgId = response.vendor_organization.id;
    setProperty({ newOrgCreated: true, updateOrgId: vendorOrgId });
    await submitVendorProfile(vendorOrgId);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (newVendorOrgParams && !newOrgCreated) {
      await submitVendorOrg();
    } else if (updateOrgId) {
      await submitVendorProfile(updateOrgId);
    }

    setIsSubmitting(false);
  };

  const goBack = () => {
    setProperty({ currentOrgStep, vendorOrg: undefined });
    updateOnboardStep('intro');
  };

  const fieldAndValue = (label: string, value: string) => {
    return (
      <div className={style.marginTopLg}>
        <Row>
          <Col>{label}</Col>
        </Row>
        <Row>
          <Col>
            <Typography.Text className={style.vendorValue} strong>
              {value}
            </Typography.Text>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <FloatingPanel colSizes={{ lg: 12, md: 14, sm: 16, xl: 8, xs: 24 }}>
      <div className={style.titleContainer}>
        <h4 className={style.title}>
          {t('onboarding.supplier.confirm_profile_info')}
        </h4>
      </div>
      { loading ? <Loading /> : (<>
        <div className={style.rowContainer}>
          <div className={style.nameContainer}>
            <div className={style.halfWidth}>
              {fieldAndValue(t('general.first_name'), userParams.firstName)}
            </div>
            <div className={style.halfWidth}>
              {fieldAndValue(t('general.last_name'), userParams.lastName)}
            </div>
          </div>

          {fieldAndValue(t('general.email_address'), currentUser.email)}
          {fieldAndValue(t('general.job_title'), userParams.jobTitle)}
          {fieldAndValue(t('general.organization_name'), vendorOrg.name)}
          {fieldAndValue(t('general.organization_description'), vendorOrg.description)}
          {fieldAndValue(t('general.organization_website'), vendorOrg.website)}
        </div>

        <div className={style.buttons}>
          <Button
            disabled={progress}
            ghost
            onClick={goBack}
            type='primary'
          >
            {t('general.back')}
          </Button>
          <Button
            disabled={progress || isSubmitting}
            onClick={handleSubmit}
            type='primary'
          >
            {t('onboarding.supplier.create_profile_button')}
          </Button>
        </div>
      </>)}
      <Footer />
      { newOrgCreated && newVendorOrgParams?.publicListed && (
        <img
          alt=''
          height='1'
          src='https://px.ads.linkedin.com/collect/?pid=991332&fmt=gif'
          style={{ display: 'none' }}
          width='1'
        />
      )}
    </FloatingPanel>
  );
};
